import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import {
  isCurrencyDisplay,
  setCurrencyDisplay,
  type CurrencyDisplay,
} from '@/lib/currency'
import {
  DEFAULT_UI_LANGUAGE,
  isUiLanguage,
  resolveBootstrapLanguage,
  storeUiLanguage,
  type UiLanguage,
} from '@/lib/uiLanguage'
import {
  DEFAULT_ACCOUNT_TIMEZONE,
  normalizeAccountTimezone,
} from '@/lib/timezones'
import { setAccountTimezone } from '@/lib/accountTimezone'

interface UseAccountRegionalSettingsReturn {
  currencyDisplay: CurrencyDisplay
  uiLanguage: UiLanguage
  timezone: string
  loading: boolean
  saving: boolean
  error: string | null
  refetch: () => Promise<void>
  updateCurrencyDisplay: (value: CurrencyDisplay) => Promise<boolean>
  updateUiLanguage: (value: UiLanguage) => Promise<boolean>
  updateTimezone: (value: string) => Promise<boolean>
}

/**
 * Preferências regionais da conta (moeda + idioma + timezone).
 *
 * Lê direto de `accounts` — o objeto de sessão pode estar defasado no localStorage.
 */
export function useAccountRegionalSettings(): UseAccountRegionalSettingsReturn {
  const { user } = useAuth()
  const { i18n } = useTranslation()
  const accountId = user?.account_id || user?.account?.id
  const [currencyDisplay, setCurrencyDisplayState] = useState<CurrencyDisplay>('BRL')
  const [uiLanguage, setUiLanguageState] = useState<UiLanguage>(DEFAULT_UI_LANGUAGE)
  const [timezone, setTimezoneState] = useState(DEFAULT_ACCOUNT_TIMEZONE)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * `persist` is deliberate: only a language that came from the account may be remembered.
   * Writing on every call would let the "no account yet" path (every public screen) clobber
   * the stored preference with the pt-BR default.
   */
  const applyUiLanguage = useCallback(
    (value: UiLanguage, { persist }: { persist: boolean }) => {
      setUiLanguageState(value)
      if (persist) {
        // Remembered so the public screens (login, password recovery) boot in the language the
        // user actually works in — they never get to read accounts.ui_language.
        storeUiLanguage(value)
      }
      if (i18n.language !== value) {
        void i18n.changeLanguage(value)
      }
    },
    [i18n]
  )

  /** React state and the module singleton must move together — non-React helpers read the latter. */
  const applyTimezone = useCallback((value: string) => {
    const normalized = normalizeAccountTimezone(value)
    setTimezoneState(normalized)
    setAccountTimezone(normalized)
  }, [])

  const fetchSettings = useCallback(async () => {
    if (!supabase || !accountId) {
      setLoading(false)
      // Public screens run here. Currency and timezone belong to the account, so they reset;
      // the language does not — it keeps whatever the boot resolved from storage or the
      // browser. Forcing pt-BR here would make the pre-login language pointless.
      //
      // Resolved from storage, not from `i18n.language`: reading the value this effect is
      // about to write made the two chase each other. Any language change re-ran the effect
      // with the previous render's closure, which then put the old language back.
      applyUiLanguage(resolveBootstrapLanguage(), { persist: false })
      applyTimezone(DEFAULT_ACCOUNT_TIMEZONE)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('accounts')
        .select('currency_display, ui_language, timezone')
        .eq('id', accountId)
        .maybeSingle()

      if (fetchError) throw fetchError

      const nextCurrency = isCurrencyDisplay(data?.currency_display)
        ? data.currency_display
        : 'BRL'

      const nextLanguage = isUiLanguage(data?.ui_language)
        ? data.ui_language
        : DEFAULT_UI_LANGUAGE

      const nextTimezone = normalizeAccountTimezone(data?.timezone)

      setCurrencyDisplayState(nextCurrency)
      setCurrencyDisplay(nextCurrency)
      applyUiLanguage(nextLanguage, { persist: true })
      applyTimezone(nextTimezone)
    } catch (err: any) {
      console.error('❌ [useAccountRegionalSettings] Erro ao carregar:', err)
      setError(err?.message ?? 'Erro ao carregar preferências regionais')
    } finally {
      setLoading(false)
    }
  }, [accountId, applyUiLanguage, applyTimezone])

  const updateCurrencyDisplay = useCallback(
    async (value: CurrencyDisplay): Promise<boolean> => {
      if (!supabase || !accountId) return false

      const previous = currencyDisplay
      setSaving(true)
      setError(null)
      setCurrencyDisplayState(value)
      setCurrencyDisplay(value)

      try {
        const { error: updateError } = await supabase
          .from('accounts')
          .update({ currency_display: value, updated_at: new Date().toISOString() })
          .eq('id', accountId)

        if (updateError) throw updateError
        return true
      } catch (err: any) {
        console.error('❌ [useAccountRegionalSettings] Erro ao salvar:', err)
        setError(err?.message ?? 'Erro ao salvar preferência de moeda')
        setCurrencyDisplayState(previous)
        setCurrencyDisplay(previous)
        return false
      } finally {
        setSaving(false)
      }
    },
    [accountId, currencyDisplay]
  )

  const updateUiLanguage = useCallback(
    async (value: UiLanguage): Promise<boolean> => {
      if (!supabase || !accountId) return false

      const previous = uiLanguage
      setSaving(true)
      setError(null)
      applyUiLanguage(value, { persist: true })

      try {
        const { error: updateError } = await supabase
          .from('accounts')
          .update({ ui_language: value, updated_at: new Date().toISOString() })
          .eq('id', accountId)

        if (updateError) throw updateError
        return true
      } catch (err: any) {
        console.error('❌ [useAccountRegionalSettings] Erro ao salvar idioma:', err)
        setError(err?.message ?? 'Erro ao salvar preferência de idioma')
        applyUiLanguage(previous, { persist: true })
        return false
      } finally {
        setSaving(false)
      }
    },
    [accountId, applyUiLanguage, uiLanguage]
  )

  const updateTimezone = useCallback(
    async (value: string): Promise<boolean> => {
      if (!supabase || !accountId) return false
      const next = normalizeAccountTimezone(value)
      if (!next) return false

      const previous = timezone
      setSaving(true)
      setError(null)
      applyTimezone(next)

      try {
        const { error: updateError } = await supabase
          .from('accounts')
          .update({ timezone: next, updated_at: new Date().toISOString() })
          .eq('id', accountId)

        if (updateError) throw updateError

        // The mirror onto account_assistant_calendar_settings is guaranteed by the
        // trg_accounts_sync_assistant_timezone trigger — nothing to do from here.
        return true
      } catch (err: any) {
        console.error('❌ [useAccountRegionalSettings] Erro ao salvar timezone:', err)
        setError(err?.message ?? 'Erro ao salvar fuso horário')
        applyTimezone(previous)
        return false
      } finally {
        setSaving(false)
      }
    },
    [accountId, applyTimezone, timezone]
  )

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  return {
    currencyDisplay,
    uiLanguage,
    timezone,
    loading,
    saving,
    error,
    refetch: fetchSettings,
    updateCurrencyDisplay,
    updateUiLanguage,
    updateTimezone,
  }
}
